//SelectOnMap.tsx ==============================================================
import "../Util/Prototypes";

import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as GetSearchResults from "../Search/GetSearchResults";
import * as GraLay from "../Util/GraLay";
import * as Highlight from "../Util/Highlight";
import * as MainMap from "../MainMap";
import * as NoRecordsFound from "../Search/NoRecordsFound";
import * as Search from "../Search/Search";
import * as SelectionMode from "../Search/SelectionMode";
import * as SelectOnMap from "../Search/SelectOnMap";
import * as Settings from "../Settings";
import * as Spinner from "../Util/Spinner";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";
import * as Util_MUI from "../Util/Util_MUI";
import * as WidgetHeading from "../Widgets/WidgetHeading";

import { Button, ButtonGroup, ButtonProps, Checkbox, FormControl, FormControlLabel, Icon, Input, InputLabel, Paper, TextField, Typography } from "@mui/material";

//ESRI javascript API.
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import Graphic from "@arcgis/core/Graphic";

import React from "react";

const toolName: string = "Select on Map";

//Cursor should be default, unless "freehand" drawing mode is enabled, then crosshair.
type createToolType = "point" | "multipoint" | "polyline" | "polygon" | "rectangle" | "circle" | "mesh";
type drawingModeType = "hybrid" | "freehand" | "click";

export let selectableLayer: string = "All Visible Layers"; // Select on map selectable layer name (Must be on) or "All Visible Layers" (must be visible)
export let geom: __esri.Geometry = null;
export let active: boolean = false; // True means this tool is active.
export let createTool: createToolType = "point"; //point, polyline, polygon, rectangle, circle, or "".
export let createSketchVM: boolean = true; //Rebuild the view model when new map view.

//Setters.
export function setGeom(geometry: __esri.Geometry) {
   geom = geometry;
}
export function setCreateSketchVM(createVM: boolean) {
   createSketchVM = createVM;
}

let sketchVM: __esri.SketchViewModel;
let selectLays: string[]; //List of selectable layer names.

let drawingMode: drawingModeType = "click"; //"click" or "freehand"
let addInitBuffer: boolean = false;
let enableFreehand: boolean = false;
let toggleFreehand: boolean = false;
let bufferDisabled: boolean = true;
let helpMsg: string = "Click on the map to select by point";

let nearbyDist: number = 100;
let nearbyDistAny: unknown = "100";
let nearbyDistError: boolean = false;

let initBuffDist: number = 100;
let initBuffDistAny: unknown = "100";
let initBuffDistError: boolean = false;

let pointButtonColor: ButtonProps["color"] = "secondary";
let lineButtonColor: ButtonProps["color"] = "primary";
let polygonButtonColor: ButtonProps["color"] = "primary";
let rectangleButtonColor: ButtonProps["color"] = "primary";
let circleButtonColor: ButtonProps["color"] = "primary";

//Clear this tool.
export function clearTool() {
   if (sketchVM) {
      sketchVM.cancel();
   }
   active = false;
}

interface iProps {
}

//Render function.
export function Render(props: iProps) {
   //Util.logMsg("Top of SelectOnMap Render");

   //Initialize selectable layer list only one time.
   if (typeof selectLays === "undefined") {
      selectLays = Settings.selectableLayers.slice(0); //Clone array.
      selectLays.InsertAt(0, "All Visible Layers");
   }

   //Wire up component for each time render is called, but not on refresh or fade out.
   const [initComponent, setState] = React.useState(() => {
      Search.setActiveSearchTool(toolName);
      active = true;
   });

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Only process when the active view has changed.
   if (SelectOnMap.createSketchVM) {

      //Load the sketch view model module.
      import("@arcgis/core/widgets/Sketch/SketchViewModel").then(({ default: SketchViewModel }) => {
         createSketchVM = false;

         //Create sketch view model.
         sketchVM = new SketchViewModel({
            layer: GraLay.temp,
            view: MainMap.activeView,
            updateOnGraphicClick: false,
            tooltipOptions: { enabled: true },
         });

         //Fire up the previous drawing tool.
         if (sketchVM && !sketchVM.activeTool && SelectOnMap.createTool) {
            sketchVM.create(SelectOnMap.createTool);
         }

         //Map click event for the sketch view model.
         sketchVM.on("create", function (event: __esri.SketchViewModelCreateEvent) {
            const prevMsg: string = helpMsg;

            if (event.state === "start") { //First point.

               if (SelectOnMap.createTool === "polyline") {
                  helpMsg = "Click to continue line or double-click to finish";
                  if (enableFreehand) {
                     helpMsg = "Drag on the map to draw a line";
                  }

               } else if (SelectOnMap.createTool === "polygon") {
                  helpMsg = "Click to continue shape or double-click to finish";

               } else if (SelectOnMap.createTool === "rectangle") {
                  helpMsg = "Drag to set size of rectangle. Release to finish.";

               } else if (SelectOnMap.createTool === "circle") {
                  helpMsg = "Drag to set radius of circle. Release to finish.";
               }

            } else if (event.state === "complete") { //Double click occurred or it was a point graphic.

               if (SelectOnMap.createTool === "point") {
                  sketchVM.create(SelectOnMap.createTool);

               } else if (SelectOnMap.createTool === "polyline") {
                  sketchVM.create(SelectOnMap.createTool, { mode: drawingMode });

                  if (!enableFreehand) {
                     helpMsg = "Click on the map to start a line";
                  }

               } else if (SelectOnMap.createTool === "polygon") {
                  sketchVM.create(SelectOnMap.createTool, { mode: drawingMode });
                  helpMsg = "Click on the map to start a shape";

               } else if (SelectOnMap.createTool === "rectangle") {
                  sketchVM.create(SelectOnMap.createTool, { mode: drawingMode });
                  helpMsg = "Click, drag, and release on map to draw a rectangle";

               } else { //Circle.
                  sketchVM.create(SelectOnMap.createTool, { mode: drawingMode });
                  helpMsg = "Click, drag, and release on map to draw a circle.";
               }

               //Use the geometry to query features that intersect it.
               geom = event.graphic.geometry;

               if (addInitBuffer) {
                  const geoms: __esri.Geometry[] = [];
                  geoms.Add(SelectOnMap.geom);

                  //Create buffer with geometry engine.
                  let polys: __esri.Polygon | __esri.Polygon[];

                  if (MainMap.activeView.spatialReference.isWebMercator) {
                     polys = geometryEngine.geodesicBuffer(geoms, [initBuffDist], "feet", true);
                  } else {
                     polys = geometryEngine.buffer(geoms, [initBuffDist], "feet", true);
                  }
                  geom = polys[0] as __esri.Geometry;

                  const sym = { type: "simple-fill", color: [136, 255, 153, 0.6], style: "solid", outline: { color: [0, 0, 0, 1], width: 1 } };
                  const gra = new Graphic({ geometry: SelectOnMap.geom, symbol: sym });
                  GraLay.temp.add(gra);
               }

               GetSearchResults.init();
            }

            if (helpMsg !== prevMsg) {
               refresh(); //Warning caused by this render here!
            }
         }); //end sketch view model on create event.

      }).catch((error: Error) => {
         console.error('An error occurred while loading the Sketch View Model module:', error);
      });

   } //end create sketch view model true/false flag.

   //Component did mount.
   React.useEffect(() => {
      if (sketchVM && !sketchVM.activeTool && SelectOnMap.createTool) {
         sketchVM.create(SelectOnMap.createTool); //Fire up the previous drawing tool.
      }
   }, []);

   //Close this tool.
   function close() {
      DrawerMenu.closeTool();
   }

   //Geometry create tool changed.
   function toolChanged(inTool: createToolType, activate: boolean) {
      active = true;

      pointButtonColor = "primary";
      lineButtonColor = "primary";
      polygonButtonColor = "primary";
      rectangleButtonColor = "primary";
      circleButtonColor = "primary";

      //Disable tool.
      if (!toggleFreehand) {
         if (!activate && inTool === SelectOnMap.createTool) {
            createTool = null;
            sketchVM.cancel();
            active = false;
            helpMsg = "";
            refresh();
            return;
         }
      }
      toggleFreehand = false;

      createTool = inTool;

      if (inTool === "point") {
         pointButtonColor = "secondary";
         sketchVM.create("point");
         helpMsg = "Click on the map to select by point";

      } else if (inTool === "polyline") {
         lineButtonColor = "secondary";
         sketchVM.create(inTool, { mode: drawingMode });
         helpMsg = "Click on the map to start a line";
         if (enableFreehand) {
            helpMsg = "Drag on the map to draw a line";
         }

      } else if (inTool === "polygon") {
         polygonButtonColor = "secondary";
         sketchVM.create(inTool, { mode: drawingMode });
         helpMsg = "Click on the map to start a shape";
         if (enableFreehand) {
            helpMsg = "Drag on the map to draw a shape";
         }

      } else if (inTool === "rectangle") {
         rectangleButtonColor = "secondary";
         sketchVM.create(inTool, { mode: drawingMode });
         helpMsg = "Click, drag, and release on map to draw a rectangle";

      } else if (inTool === "circle") {
         circleButtonColor = "secondary";
         sketchVM.create(inTool, { mode: drawingMode });
         helpMsg = "Click, drag, and release on map to draw a circle.";
      }
      refresh();
   }

   //Freehand drawing mode changed.
   function drawingModeChanged(event: React.ChangeEvent<HTMLInputElement>) {
      const checked: boolean = event.target.checked;
      enableFreehand = checked;

      drawingMode = "click";
      if (checked) {
         drawingMode = "freehand";
      }

      toggleFreehand = true;
      toolChanged(SelectOnMap.createTool, false);
   }

   //Selectable layer changed.
   function selectableLayerChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      selectableLayer = event.target.value;

      if (SelectOnMap.selectableLayer !== "All Visible Layers") {
         Util_App.setActiveLayer(SelectOnMap.selectableLayer);
      }

      //Highlight.updateParameters();
      refresh();
   }

   //Select all features in current view extent.
   function selectCurrentView() {
      geom = MainMap.activeView.extent;
      GetSearchResults.init();
   }

   //Select the maximum number of features.
   function selectAll() {
      if (SelectionMode.value === "Remove" && SelectOnMap.selectableLayer === "All Visible Layers") {
         GetSearchResults.clearAllResults();
         DrawerMenu.showTool(Search.activeSearchTool);
         NoRecordsFound.setMsg('All results cleared. Change the selection type to "New" or "Add"');
         return;
      }
      geom = null;
      GetSearchResults.init();
   }

   //Select nearby distance changed.
   function nearbyDistChanged(event: React.ChangeEvent<HTMLInputElement>) {
      const value = Util.parseInteger(event.target.value);

      nearbyDist = 0;
      nearbyDistAny = "";
      nearbyDistError = false;

      if (value) {  // && value > 0) {
         nearbyDist = value;
         nearbyDistAny = value;
      } else {
         nearbyDistError = true;
      }
      refresh();
   }

   //Initial buffer distance changed.
   function initBuffDistChanged(event: React.ChangeEvent<HTMLInputElement>) {
      const value = Util.parseInteger(event.target.value);

      initBuffDist = 0;
      initBuffDistAny = "";
      initBuffDistError = false;

      if (value && value > 0) {
         initBuffDist = value;
         initBuffDistAny = value;
      } else {
         initBuffDistError = true;
      }
      refresh();
   }

   //Get buffer select data to select nearby features.
   function selectNearby() {
      if (Spinner.busy || Search.grandTotRec === 0) {
         return; //Quit if busy.
      }

      //Buffer the active layer.
      const lay = Search.layers[Search.activeLayerIndex];

      const geoms: __esri.Geometry[] = [];
      for (const feature of lay.fs.features) {
         geoms.Add(feature.geometry);
      }

      //Create buffer with geometry engine.
      let polys: __esri.Polygon | __esri.Polygon[];

      if (MainMap.activeView.spatialReference.isWebMercator) {
         polys = geometryEngine.geodesicBuffer(geoms, [nearbyDist], "feet", true);
      } else {
         polys = geometryEngine.buffer(geoms, [nearbyDist], "feet", true);
      }
      //SelectOnMap.geom = SelectOnMap.geom[0];

      let poly = polys[0] as __esri.Polygon;
      geom = poly as __esri.Geometry;

      Util.logMsg("selectNearby buffer created");
      const sym = { type: "simple-fill", color: [136, 255, 153, 0.6], style: "solid", outline: { color: [0, 0, 0, 1], width: 1 } };
      const gra = new Graphic({ geometry: SelectOnMap.geom, symbol: sym });
      GraLay.temp.add(gra);

      GetSearchResults.init();
   }

   //Add initial buffer checkbox changed.
   function addInitBufferChanged(event: React.ChangeEvent<HTMLInputElement>) {
      addInitBuffer = event.target.checked;
      refresh();
   }

   //Show error message.
   function errMsg(msg: string, error?: object): void {
      if (error) {
         Util.errMsg(msg, error);
      } else {
         Util.errMsg(msg);
      }
      Spinner.hide();
   }

   const scrollDivID: string = toolName.replace(" ", "_").replace(" ", "_") + "_Scroll";

   if (!SelectOnMap.selectableLayer) {
      selectableLayer = "All Visible Layers";
   }

   bufferDisabled = false;
   if (Search.grandTotRec === 0) {
      bufferDisabled = true;
   }

   return (
      <Paper>
         <WidgetHeading.Render title={toolName} onClose={close} />

         <div id={scrollDivID} style={Default.styles.appToolDiv}>
            <div style={{ marginLeft: 7, marginTop: 7, marginRight: 50 }}>
               <TextField select fullWidth
                  label="Select from"
                  title="Select from this map layer"
                  inputProps={{ id: "select-from-som" }}
                  InputLabelProps={{ htmlFor: "select-from-som" }}
                  value={SelectOnMap.selectableLayer}
                  onChange={selectableLayerChanged}
               >
                  {Util_MUI.buildSelectMenuItems(selectLays)}
               </TextField>
            </div>

            <ButtonGroup style={{ marginTop: 15 }} variant="contained" fullWidth aria-label="Toolbar buttons">

               <Button color={pointButtonColor} onClick={() => toolChanged("point", false)} title="Select by clicking a point on the map" aria-label="Select by point">
                  <Icon>place</Icon>
               </Button>

               <Button color={lineButtonColor} onClick={() => toolChanged("polyline", false)} title="Select by drawing a line on the map" aria-label="Select by line">
                  <Icon>timeline</Icon>
               </Button>

               <Button color={polygonButtonColor} onClick={() => toolChanged("polygon", false)} title="Select by drawing a shape on the map" aria-label="Select by shape">
                  <Icon>turned_in_not</Icon>
               </Button>

               <Button color={rectangleButtonColor} onClick={() => toolChanged("rectangle", false)} title="Select by dragging a rectangle on the map" aria-label="Select by rectangle">
                  <Icon>check_box_outline_blank</Icon>
               </Button>

               <Button color={circleButtonColor} onClick={() => toolChanged("circle", false)} title="Select by dragging a circle on the map" aria-label="Select by circle">
                  <Icon>radio_button_unchecked</Icon>
               </Button>
            </ButtonGroup>

            <Typography variant="body2" style={{ marginTop: 15 }}>
               {helpMsg}
            </Typography>

            <FormControlLabel title="Enable freehand drawing"
               control={<Checkbox inputProps={{ id: "freehand-drawing" }} checked={enableFreehand} color="primary" onChange={drawingModeChanged} />}
               label="Enable freehand drawing"
            />

            <div>
               <div className="app-flex-row-between" style={{ marginTop: 9 }}>
                  <Button variant="outlined" size="medium" color="info" onClick={selectCurrentView} title="Select all features in the current map view" aria-label="Select current view">
                     <Icon color="action">stay_current_landscape</Icon>
                     <span style={{ marginLeft: 6 }}></span>
                     Current View
                  </Button>

                  <Button style={{ marginRight: 9 }} variant="outlined" size="medium" color="info" onClick={selectAll} title="Select all features in the selectable map layer" aria-label="Select all">
                     <Icon color="action">select_all</Icon>
                     <span style={{ marginLeft: 6 }}></span>
                     Select All
                  </Button>
               </div>

               <div className="app-flex-row-start" style={{ marginTop: 15 }}>
                  <FormControlLabel title="Add initial buffer"
                     control={<Checkbox inputProps={{ id: "initial-buffer" }} checked={addInitBuffer} color="primary" onChange={addInitBufferChanged} />}
                     label="Add initial buffer"
                  />

                  <FormControl style={{ width: 100, marginLeft: 19 }} title="Buffer distance">
                     <InputLabel id="init-buff-dist-label" htmlFor="init-buff-dist">Distance (ft)</InputLabel>
                     <Input
                        id="init-buff-dist"
                        margin="dense"
                        type="number"
                        value={initBuffDistAny}
                        onChange={initBuffDistChanged}
                        error={initBuffDistError}
                     />
                  </FormControl>
               </div>

               <div className="app-flex-row-start" style={{ marginTop: 15 }}>
                  <Button variant="outlined" size="medium" color="info" onClick={selectNearby} disabled={bufferDisabled} title="Select nearby map features within the specified distance" aria-label="Select nearby within:">
                     Select nearby within:
                  </Button>

                  <FormControl disabled={bufferDisabled} style={{ width: 100, marginLeft: 19 }} title="Buffer distance from currently selected features">
                     <InputLabel id="nearby-dist-label" htmlFor="nearby-dist">Distance (ft)</InputLabel>
                     <Input
                        id="nearby-dist"
                        margin="dense"
                        type="number"
                        value={nearbyDistAny}
                        onChange={nearbyDistChanged}
                        error={nearbyDistError}
                     />
                  </FormControl>
               </div>
            </div>

            <NoRecordsFound.Render />
            <SelectionMode.Render tool="SelectOnMap" />
         </div>
      </Paper>

   ); //end return.

} //end function.
